import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, User, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import Logo from '@/components/site/logo'
import ThemeToggle from '@/components/site/theme-toggle'
import { mainNav } from '@/constants/site'

interface HeaderProps {
    transparent?: boolean
}

const Header: FC<HeaderProps> = ({ transparent = false }): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const overlay = transparent && !scrolled

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500',
                overlay
                    ? 'border-b border-transparent bg-transparent'
                    : 'border-b border-border/70 bg-background/85 backdrop-blur-md supports-backdrop-filter:bg-background/75'
            )}
        >
            <div className='mx-auto flex h-18 max-w-[1440px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12'>
                <Link href='/' aria-label='ArtDoors, naar de homepage' className='shrink-0 rounded-sm focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'>
                    <Logo tone={overlay ? 'light' : 'dark'} />
                </Link>

                <nav aria-label='Hoofdmenu' className='hidden items-center gap-1 lg:flex'>
                    {mainNav.map(item => (
                        <Link
                            key={item.href}
                            href={item.href}
                            className={cn(
                                'relative rounded-sm px-3 py-2 text-[0.82rem] font-medium tracking-wide transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                overlay ? 'text-ink-foreground/85 hover:text-ink-foreground' : 'text-foreground/70 hover:text-foreground',
                                isActive(item.href) && (overlay ? 'text-ink-foreground' : 'text-foreground')
                            )}
                        >
                            {item.label}

                            {isActive(item.href) && (
                                <span className='absolute inset-x-3 -bottom-0.5 h-px bg-brass' aria-hidden='true' />
                            )}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <ThemeToggle className='mr-1' />

                    <Link
                        href='/account'
                        className={cn(
                            'hidden items-center gap-2 rounded-sm px-3 py-2 text-[0.82rem] font-medium transition-colors sm:inline-flex focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                            overlay ? 'text-ink-foreground/85 hover:text-ink-foreground' : 'text-foreground/70 hover:text-foreground'
                        )}
                    >
                        <User className='size-4' aria-hidden='true' />
                        Mijn account
                    </Link>

                    <Link
                        href='/configurator'
                        className={cn(
                            'hidden items-center gap-2 rounded-sm px-4 py-2.5 text-[0.8rem] font-semibold tracking-wide transition-colors md:inline-flex focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                            'bg-primary text-primary-foreground hover:bg-primary/85'
                        )}
                    >
                        Ontwerp uw deur
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(true)}
                        aria-label='Menu openen'
                        className={cn(
                            'inline-flex size-11 items-center justify-center rounded-sm transition-colors lg:hidden focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                            overlay ? 'text-ink-foreground hover:bg-white/10' : 'text-foreground hover:bg-muted'
                        )}
                    >
                        <Menu className='size-6' aria-hidden='true' />
                    </button>
                </div>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side='right' className='w-full max-w-sm bg-background p-0'>
                    <div className='flex h-18 items-center border-b border-border px-6'>
                        <SheetTitle className='sr-only'>Menu</SheetTitle>
                        <Logo />
                    </div>

                    <nav aria-label='Mobiel menu' className='flex flex-col px-3 py-4'>
                        {mainNav.map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cn(
                                    'flex items-center justify-between rounded-sm px-3 py-3.5 font-display text-2xl transition-colors hover:bg-muted',
                                    isActive(item.href) ? 'text-foreground' : 'text-foreground/75'
                                )}
                            >
                                {item.label}
                                <ArrowRight className='size-4 text-brass' aria-hidden='true' />
                            </Link>
                        ))}
                    </nav>

                    <div className='mt-auto flex flex-col gap-3 border-t border-border p-6'>
                        <Link href='/configurator' className='inline-flex h-12 items-center justify-center gap-2 rounded-sm bg-primary text-sm font-semibold text-primary-foreground'>
                            Ontwerp uw deur
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>

                        <Link href='/account' className='inline-flex h-12 items-center justify-center gap-2 rounded-sm border border-border text-sm font-medium'>
                            <User className='size-4' aria-hidden='true' />
                            Mijn account
                        </Link>
                    </div>
                </SheetContent>
            </Sheet>
        </header>
    )
}

export default Header
